import type { ConnectionItemProps } from "@wayflyer/flyui";
import { Image } from "@wayflyer/flyui";
import { IconShop24Line } from "@wayflyer/flyui-icons/24/line";
import { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { CONNECT_APP_ROUTE_PATHS } from "../../../components/constants";
import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";

const getIcon = (connector: FundingApplicationSchemas.ConnectorPropertiesSchema) => {
  return connector.slug === "codat_accounting" ? (
    <IconShop24Line />
  ) : connector.connectionLogoSrc ? (
    <Image src={connector.connectionLogoSrc} />
  ) : null;
};

export const usePlatformConnectionsAsConnectionItems = (
  connectors: FundingApplicationSchemas.ConnectorPropertiesSchema[],
  fundingApplication: FormattedFundingApplicationScreen,
  removePlatform: (slug: string) => void
): { completedItems: ConnectionItemProps[]; incompleteItems: ConnectionItemProps[] } => {
  const { navigate } = useSpaNavigate("FundingApp");
  const intl = useIntl();
  const completedItems: ConnectionItemProps[] = connectors
    .filter((connector) => connector.connections.length)
    .map((connector) => {
      return {
        testId: `connection-${connector.slug}`,
        label: connector.name,
        subtitle: (
          <ul>
            {connector.connections.map((connection) => (
              <li key={connector.name}>{connection.identifierName || connector.name}</li>
            ))}
          </ul>
        ),
        logo: getIcon(connector),
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.connected),
      } satisfies ConnectionItemProps;
    });
  const incompleteItems: ConnectionItemProps[] = connectors
    .filter((connector) => !connector.connections.length)
    .map((connector) => {
      return {
        testId: `connection-${connector.slug}`,
        label:
          connector.slug === "codat_accounting" ? fundingApplication.selectedUnsupportedAccounting[0] : connector.name,
        logo: getIcon(connector),
        actions: [
          {
            critical: true,
            content: intl.formatMessage({
              id: "funding-application: remove platform label",
              defaultMessage: "Remove",
            }),
            onAction: () => removePlatform(connector.slug),
            analyticsId: `remove-${connector.slug}`,
          },
          {
            content: intl.formatMessage(COMMON_MESSAGES.connect),
            onAction: () =>
              navigate(
                `${CONNECT_APP_ROUTE_PATHS.CONNECTIONS}/${CONNECT_APP_ROUTE_PATHS.ADD_CONNECTION}?platform=${connector.slug}`
              ),
          },
        ],
      } satisfies ConnectionItemProps;
    });

  return { completedItems, incompleteItems };
};
